import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router";

// Hooks & Helpers
import { useBreadcrumb } from "sj-ui-toolkit/src/atlas/hooks/useBreadcrumbs";
import { useToast } from "sj-ui-toolkit/src/hooks";
import {
  getEmailTemplates,
  restoreDefaultTemplate,
  sendTestEmail,
  updateEmailTemplate,
} from "sj-ui-toolkit/src/api";
import { getTemplatesData } from "@/helpers/emailTemplates";

// Routes
import { AdminAppRoutes } from "sj-ui-toolkit/src/constants/routes";

// 3rd Party
import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { Skeleton } from "primereact/skeleton";

// Components
import { EmailFrequency } from "@/components/email/EmailFrequency";
import { EmailTemplateBody } from "@/components/email/EmailTemplateBody";
import { EmailTemplateContext } from "@/components/email/EmailTemplateContext";
import { SendTestEmailDialog } from "@/components/email/SendTestEmailDialog";
import { EmailNotificationFooter } from "@/components/email/elements/EmailNotificationFooter";
import { EmailNotificationHeader } from "@/components/email/elements/EmailNotificationHeader";

// Types
import type { EmailTemplateData } from "@/components/email/types";
import type { EmailTemplateItem } from "sj-ui-toolkit/src/api/admin/comms.email";
import type { RefMap } from "@/components/email/EmailTemplateContext";

// Styles & Assets
import S from "@/components/email/templates/Content.module.scss";
import banner from "@/assets/images/email/banner.jpg";

export function CommsEmailEdit() {
  const { showToast } = useToast();
  const { template: templateName } = useParams<{ template: string }>();

  const [templates, setTemplates] = useState<EmailTemplateData[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogVisible, setDialogVisible] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const hasLoadedRef = useRef(false);

  useEffect(() => {
    if (hasLoadedRef.current) return;
    hasLoadedRef.current = true;

    const loadTemplates = async () => {
      try {
        const templateData = await getEmailTemplates();
        setTemplates(templateData);
      } catch (error) {
        console.error("Failed to load email templates:", error);
        showToast({ severity: "error", detail: "Failed to load email templates" });
      } finally {
        setLoading(false);
      }
    };

    loadTemplates();
  }, []);

  const template = useMemo(
    () => templates.find(t => t.name === templateName) ?? null,
    [templates, templateName]
  );

  useBreadcrumb(
    template
      ? {
          label: template.title,
          route: `${AdminAppRoutes.routes.comms.routes.email.route}/${templateName}`,
          display: "page",
        }
      : null
  );

  // One ref slot per template item, read by getTemplatesData at save time
  const data: RefMap = useMemo(() => {
    if (!template?.template) {
      return { current: {} };
    }
    return {
      current: Object.fromEntries(
        Array.from({ length: template.template.length }, (_, i) => [i, { current: null }])
      ),
    };
  }, [template?.template?.length]);

  const onSave = useCallback(
    async (id: number, refMap: RefMap, templateItems: EmailTemplateItem[]) => {
      if (isSubmitting) return;
      setIsSubmitting(true);
      try {
        const transformed = getTemplatesData(refMap, templateItems);
        await updateEmailTemplate({
          id,
          template: transformed,
        });
        showToast({ severity: "success", detail: "Template updated successfully" });
      } catch (error) {
        console.error("Error updating template:", error);
        showToast({ severity: "error", detail: "Error updating template" });
      } finally {
        setIsSubmitting(false);
      }
    },
    [isSubmitting, showToast]
  );

  const onRestore = async (id: number) => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    try {
      const { data: restored } = await restoreDefaultTemplate(id);
      setTemplates(prev => prev.map(t => (t.id === id ? restored : t)));
      showToast({ severity: "success", detail: "Template restored to default" });
    } catch (error) {
      console.error("Failed to restore template:", error);
      showToast({ severity: "error", detail: "Failed to restore template to default" });
    } finally {
      setIsSubmitting(false);
    }
  };

  const onSendTestEmail = async (email: string, name: string) => {
    try {
      await sendTestEmail({ email, templateName: name });
      showToast({ severity: "success", detail: `A test email has been sent to ${email}` });
    } catch (error) {
      console.error("Error sending test email:", error);
      showToast({ severity: "error", detail: "Failed to send test email" });
      throw error;
    }
  };

  if (loading) {
    return (
      <Card title={<Skeleton width="30%" height="3rem" />}>
        <div className="flex flex-col gap-4">
          <Skeleton width="80%" height="2rem" />
          <Skeleton width="100%" height="4rem" />
        </div>
      </Card>
    );
  }

  if (!template) {
    return <div>Template not found</div>;
  }

  return (
    <EmailTemplateContext.Provider value={{ data, template, onSave }}>
      <div className="mb-4 sr-only">
        <h1>{`Emails - ${template.title}`}</h1>
      </div>
      <div className="flex flex-col gap-8">
        {template.name === "notify_user_for_inactivity_email" && <EmailFrequency />}

        <Card>
          <img src={banner} alt="Email Banner" className="w-full object-contain" />
          <EmailNotificationHeader template={template.template[0]!} innerRef={data.current[0]} />
          <div className={S.root}>
            <div>
              <EmailTemplateBody templateName={template.name} />
            </div>
          </div>
          <EmailNotificationFooter />
        </Card>
      </div>
      <div className="flex gap-4 justify-between p-4 bg-elevation-2">
        <div className="flex gap-4">
          <Button
            label="Save"
            loading={isSubmitting}
            onClick={() => void onSave(template.id, data, template.template)}
          />
          <Button
            label="Restore Default"
            severity="secondary"
            loading={isSubmitting}
            onClick={() => void onRestore(template.id)}
          />
        </div>
        <Button
          severity="secondary"
          label="Test"
          icon="pi pi-send"
          onClick={() => setDialogVisible(true)}
        />
      </div>
      <SendTestEmailDialog
        visible={dialogVisible}
        onHide={() => setDialogVisible(false)}
        onSend={onSendTestEmail}
        templateName={template.name}
        templateTitle={template.title}
      />
    </EmailTemplateContext.Provider>
  );
}
